import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ErrorResponse, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { ActivitiesSliceModel, Activity, ActivityByIdRequest, ActivityCreateData, ImportActivityRequest, ProfileLists } from "./activities-interface";
import { Guid } from "guid-typescript";
import { CreateNote, UpdateNote } from "./notesSlice";
import { CreateTask, UpdateTask } from "./tasksSlice";
import { CreateActivityLog, UpdateActivityLog } from "./activityLogsSlice";
import { API_BASE_URL as baseUri } from "config/env";

//--------------------------------------------SHARED ENDPOINTS---------------------------------------------------

export const GetActivityCreateData = createAsyncThunk<ActivityCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/GetCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/activities/get-create-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActivityCreateData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetActvitiesByContact = createAsyncThunk<ProfileLists, Guid, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/GetActvitiesByContact',
    async (contactId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activities/get-by-contact?contactId=${contactId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProfileLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetMeetingsByContact = createAsyncThunk<Activity[], Guid, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/GetMeetingsByContact',
    async (contactId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activities/get-meetings-by-contact?contactId=${contactId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetActvitiesByCompany = createAsyncThunk<ProfileLists, Guid, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/GetActvitiesByCompany',
    async (companyId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activities/get-by-company?companyId=${companyId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProfileLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetActivityById = createAsyncThunk<Activity, ActivityByIdRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/GetActivityById',
    async (activityByIdRequest: ActivityByIdRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activities/get`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(activityByIdRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteActivity = createAsyncThunk<MainResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/DeleteActivity',
    async (activityId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activities/delete?activityId=${activityId.toString()}`, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ImportHubspotActivities = createAsyncThunk<MainResponse, ImportActivityRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/ImportHubspotActivities',
    async (activities: ImportActivityRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activity-logs/import-hubspot-activities`, {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(activities)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

const initialValue: ActivitiesSliceModel = {
    activityCreateData: undefined,
    profileLists: { activities: [], contacts: [], companies: [], deals: [], requests: [] },
    activity: undefined,

    statusCode: "",
    message: "",
    showNotification: false,
    reloadData: false,

    isLoading: false,
    importActivities: {
        isLoading: false
    }
}

export const ActivitiesSlice = createSlice({
    name: 'ActivitiesSlice',
    initialState: initialValue,
    reducers: {
        resetActivitiesSlice: (state) => {
            state = initialValue
        },
        resetProfileLists: (state) => {
            state.profileLists = { activities: [], contacts: [], companies: [], deals: [], requests: [] }
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
    },
    extraReducers(builder) {
        builder.addCase(GetActivityCreateData.fulfilled, (state, action: PayloadAction<ActivityCreateData>) => {
            // state.isLoading = false;
            state.activityCreateData = action.payload
        })
            .addCase(GetActivityCreateData.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActivityCreateData.pending, state => {
                // state.isLoading = true;
            })

        builder.addCase(GetActvitiesByContact.fulfilled, (state, action: PayloadAction<ProfileLists>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.profileLists = action.payload;
        })
            .addCase(GetActvitiesByContact.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActvitiesByContact.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetMeetingsByContact.fulfilled, (state, action: PayloadAction<Activity[]>) => {
            // Keep the already rendered activities and merge in the meetings.
            // Drop any previously merged meetings first so repeated fetches stay idempotent.
            // The list component re-sorts by createdAt, so ordering is preserved.
            const activitiesWithoutMeetings = state.profileLists.activities.filter(activity => activity.activityName !== "Meeting");
            state.profileLists.activities = [...activitiesWithoutMeetings, ...action.payload];
        })
            .addCase(GetMeetingsByContact.rejected, (_state, action) => {
                // Meetings are a supplementary enrichment fetched after the activity list is
                // already rendered. If the (Graph/Teams-backed) endpoint fails, degrade gracefully:
                // keep the rendered activities and don't disrupt the user with an error toast.
                console.warn('GetMeetingsByContact failed:', action.payload?.message ?? 'Unknown error');
            })

        builder.addCase(GetActvitiesByCompany.fulfilled, (state, action: PayloadAction<ProfileLists>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.profileLists = action.payload;
        })
            .addCase(GetActvitiesByCompany.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActvitiesByCompany.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetActivityById.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.activity = action.payload;
        })
            .addCase(GetActivityById.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActivityById.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(DeleteActivity.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = "200";
            state.profileLists.activities = state.profileLists.activities.filter(activity => activity.id?.toString() !== action.payload.id);
        })
            .addCase(DeleteActivity.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteActivity.pending, state => {
                state.isLoading = true;
            })
        builder.addCase(ImportHubspotActivities.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.importActivities.isLoading = false;
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = "200";
        })
            .addCase(ImportHubspotActivities.rejected, (state, action) => {
                state.importActivities.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ImportHubspotActivities.pending, state => {
                state.importActivities.isLoading = true;
            })

        //#region Cases from other slices

        //Note
        builder.addCase(CreateNote.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.profileLists.activities = [...state.profileLists.activities, action.payload];
        })
        builder.addCase(UpdateNote.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.profileLists.activities = state.profileLists.activities.map(activity =>
                activity.id === action.payload.id ? action.payload : activity
            );
        })

        //Task
        builder.addCase(CreateTask.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.profileLists.activities = [...state.profileLists.activities, action.payload];
        })
        builder.addCase(UpdateTask.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.profileLists.activities = state.profileLists.activities.map(activity =>
                activity.id === action.payload.id ? action.payload : activity
            );
        })

        //Log
        builder.addCase(CreateActivityLog.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.profileLists.activities = [...state.profileLists.activities, action.payload];
        })
        builder.addCase(UpdateActivityLog.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.profileLists.activities = state.profileLists.activities.map(activity =>
                activity.id === action.payload.id ? action.payload : activity
            );
        })

        //#endregion
    }
}
);

export const { resetActivitiesSlice, resetProfileLists, resetStatusCode } = ActivitiesSlice.actions;
export default ActivitiesSlice.reducer;